/**
 * DeviceDetailModal
 * Read-only side-sheet showing all relevant information for a given device (POS terminal):
 *  - Device info (ID, username, last sync, status)
 *  - Associated company
 *  - Assigned branch
 */

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Monitor,
  Building2,
  Store,
  Hash,
  AtSign,
  CalendarDays,
  RefreshCw,
  KeyRound,
} from "lucide-react";
import { type Device } from "@/services/devicesService";
import { type Company } from "@/services/companiesService";
import { type Branch } from "@/services/branchesService";
import UserAvatar from "@/components/UserAvatar";

// ─── Fila de credencial ─────────────────────────────────────────────────

function CredRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="space-y-1">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40">{label}</p>
      <p className="font-mono text-xs text-emerald-300 break-all leading-relaxed select-all">
        {value || <span className="text-white/25 italic not-italic">Sin dato</span>}
      </p>
    </div>
  );
}

interface DeviceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: Device | null;
  company: Company | null;
  branch: Branch | null;
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-white/40">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-white/40 mb-0.5">{label}</p>
        <p className="text-sm text-white break-words">
          {value || <span className="text-white/30 italic">Sin dato</span>}
        </p>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">
      {children}
    </p>
  );
}

export default function DeviceDetailModal({
  isOpen,
  onClose,
  device,
  company,
  branch,
}: DeviceDetailModalProps) {
  if (!device) return null;

  const createdAt = device.created_at
    ? new Date(device.created_at).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  const lastSync = device.last_sync
    ? new Date(device.last_sync).toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header ─────────────────────────────────────── */}
        <SheetHeader
          className="flex-shrink-0 px-6 pb-4 border-b border-white/10"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-4">
            <UserAvatar role="device" size="xl" icon={<Monitor className="h-7 w-7" />} />
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {device.name}
              </SheetTitle>
              <p className="text-sm text-white/50 mt-0.5 font-mono">@{device.username}</p>
              <div className="mt-2">
                <Badge
                  className={
                    device.is_active
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                      : "bg-red-500/20 text-red-400 border-red-500/30"
                  }
                >
                  {device.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Scrollable body ─────────────────────────────── */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Información del dispositivo */}
            <section className="space-y-3">
              <SectionTitle>
                <Monitor className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Información del dispositivo
              </SectionTitle>
              <InfoRow
                icon={<Hash className="h-4 w-4" />}
                label="ID"
                value={<span className="font-mono text-xs">{device.id}</span>}
              />
              <InfoRow
                icon={<AtSign className="h-4 w-4" />}
                label="Usuario"
                value={<span className="font-mono">{device.username}</span>}
              />
              <InfoRow
                icon={<CalendarDays className="h-4 w-4" />}
                label="Registrado el"
                value={createdAt}
              />
              <InfoRow
                icon={<RefreshCw className="h-4 w-4" />}
                label="Última sincronización"
                value={lastSync ?? <span className="text-white/30 italic">Nunca</span>}
              />
            </section>

            <Separator className="bg-white/10" />

            {/* Empresa */}
            <section className="space-y-3">
              <SectionTitle>
                <Building2 className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Empresa
              </SectionTitle>
              {company ? (
                <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10 space-y-2">
                  <div className="flex items-center gap-3">
                    <UserAvatar role="company" size="sm" icon={<Building2 className="h-4 w-4" />} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{company.name}</p>
                      <Badge
                        className={
                          company.is_active
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mt-1"
                            : "bg-red-500/20 text-red-400 border-red-500/30 mt-1"
                        }
                      >
                        {company.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-white/30 italic">Sin empresa asignada</p>
              )}
            </section>

            <Separator className="bg-white/10" />

            {/* Sucursal */}
            <section className="space-y-3">
              <SectionTitle>
                <Store className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Sucursal
              </SectionTitle>
              {branch ? (
                <div className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                  <div className="flex items-center gap-3 min-w-0">
                    <UserAvatar role="branch" size="sm" icon={<Store className="h-4 w-4" />} />
                    <p className="text-sm text-white font-medium truncate">{branch.name}</p>
                  </div>
                  <Badge
                    className={
                      branch.is_active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 ml-2 flex-shrink-0"
                        : "bg-red-500/20 text-red-400 border-red-500/30 ml-2 flex-shrink-0"
                    }
                  >
                    {branch.is_active ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
              ) : (
                <p className="text-sm text-white/30 italic">Sin sucursal asignada</p>
              )}
            </section>

            <Separator className="bg-white/10" />

            {/* Credenciales */}
            <section className="space-y-3">
              <SectionTitle>
                <KeyRound className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Credenciales
              </SectionTitle>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 space-y-3">
                <CredRow label="REMOTE_SERVER_URL" value={branch?.backend_url} />
                <CredRow label="COMPANY_UUID"      value={device.company_id} />
                <CredRow label="BRANCH_UUID"       value={device.branch_id} />
                <CredRow label="DEVICE_USERNAME"   value={device.username} />
                <CredRow label="DEVICE_PASSWORD"   value="••••••••  (solo visible al crear)" />
                <CredRow label="DB_PASSWORD"       value={device.db_password} />
                <CredRow label="SYNC_LOCAL_API_TOKEN" value={device.sync_token} />
              </div>
            </section>

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
